/**
 * Reducer helpers for dynamic block authoring commands (DYN-1.7 + DYN-1.8).
 *
 *   - geometry/add-dynamic-parameter
 *   - geometry/update-dynamic-parameter
 *   - geometry/remove-dynamic-parameter
 *   - geometry/add-dynamic-action
 *   - geometry/update-dynamic-action
 *   - geometry/remove-dynamic-action
 *   - geometry/add-visibility-state
 *   - geometry/rename-visibility-state
 *   - geometry/delete-visibility-state
 *   - geometry/reorder-visibility-states
 *   - geometry/set-default-visibility-state
 *   - geometry/set-current-visibility-state    (Phase K.4)
 *   - geometry/toggle-visibility-state-entity
 *   - geometry/bvhide                          (Phase K.4)
 *   - geometry/bvshow                          (Phase K.4)
 *   - geometry/bvshow-all                      (Phase K.4)
 *
 * All handlers enforce BEDIT gating (the active space must be
 * `block-edit` and its `blockEditName` must match the command's
 * `blockName`). Mutations live in command-engine; this module adapts
 * them to the EditorCommand union and the reducer pipeline.
 */

import {
  assembleDrawingDocument,
  dynamicActionSchema,
  dynamicParameterSchema,
  geometricConstraintSchema,
  type DrawingDocument,
  type DynamicAction,
  type DynamicParameter,
  type GeometricConstraint,
} from "@dxf-ai-editor/drawing-model";
import {
  addActionToChainGroupMutation,
  addDynamicActionMutation,
  addDynamicParameterMutation,
  addGeometricConstraintMutation,
  addVisibilityStateMutation,
  bvhideMutation,
  bvshowAllMutation,
  bvshowMutation,
  createChainGroupMutation,
  deleteVisibilityStateMutation,
  removeActionFromChainMutation,
  removeDynamicActionMutation,
  removeDynamicParameterMutation,
  renameVisibilityStateMutation,
  reorderChainGroupMutation,
  reorderVisibilityStatesMutation,
  setCurrentVisibilityStateMutation,
  setDefaultVisibilityStateMutation,
  toggleVisibilityStateEntityMutation,
  updateDynamicActionMutation,
  updateDynamicParameterMutation,
} from "@dxf-ai-editor/command-engine";
import type { EditorCommand } from "@dxf-ai-editor/contracts";

type AddDynamicParameterCommand = Extract<
  EditorCommand,
  { type: "geometry/add-dynamic-parameter" }
>;
type UpdateDynamicParameterCommand = Extract<
  EditorCommand,
  { type: "geometry/update-dynamic-parameter" }
>;
type RemoveDynamicParameterCommand = Extract<
  EditorCommand,
  { type: "geometry/remove-dynamic-parameter" }
>;
type AddDynamicActionCommand = Extract<
  EditorCommand,
  { type: "geometry/add-dynamic-action" }
>;
type UpdateDynamicActionCommand = Extract<
  EditorCommand,
  { type: "geometry/update-dynamic-action" }
>;
type RemoveDynamicActionCommand = Extract<
  EditorCommand,
  { type: "geometry/remove-dynamic-action" }
>;
type AddVisibilityStateCommand = Extract<
  EditorCommand,
  { type: "geometry/add-visibility-state" }
>;
type RenameVisibilityStateCommand = Extract<
  EditorCommand,
  { type: "geometry/rename-visibility-state" }
>;
type DeleteVisibilityStateCommand = Extract<
  EditorCommand,
  { type: "geometry/delete-visibility-state" }
>;
type ReorderVisibilityStatesCommand = Extract<
  EditorCommand,
  { type: "geometry/reorder-visibility-states" }
>;
type SetDefaultVisibilityStateCommand = Extract<
  EditorCommand,
  { type: "geometry/set-default-visibility-state" }
>;
type ToggleVisibilityStateEntityCommand = Extract<
  EditorCommand,
  { type: "geometry/toggle-visibility-state-entity" }
>;
type SetCurrentVisibilityStateCommand = Extract<
  EditorCommand,
  { type: "geometry/set-current-visibility-state" }
>;
type BvhideCommand = Extract<EditorCommand, { type: "geometry/bvhide" }>;
type BvshowCommand = Extract<EditorCommand, { type: "geometry/bvshow" }>;
type BvshowAllCommand = Extract<
  EditorCommand,
  { type: "geometry/bvshow-all" }
>;
type CreateChainGroupCommand = Extract<
  EditorCommand,
  { type: "geometry/create-chain-group" }
>;
type AddActionToChainGroupCommand = Extract<
  EditorCommand,
  { type: "geometry/add-action-to-chain-group" }
>;
type RemoveActionFromChainCommand = Extract<
  EditorCommand,
  { type: "geometry/remove-action-from-chain" }
>;
type ReorderChainGroupCommand = Extract<
  EditorCommand,
  { type: "geometry/reorder-chain-group" }
>;
type AddGeometricConstraintCommand = Extract<
  EditorCommand,
  { type: "geometry/add-geometric-constraint" }
>;

/**
 * BEDIT gating + block existence check. Returns `null` when the command
 * must be ignored (no active BEDIT session or the target block name does
 * not match). The caller then returns the document unchanged.
 */
function guardBeditContext(
  document: DrawingDocument,
  blockName: string,
): boolean {
  const blockEditSpace = document.spaces.find((s) => s.kind === "block-edit");
  if (!blockEditSpace || blockEditSpace.blockEditName !== blockName) {
    return false;
  }
  if (!document.blocks[blockName]) return false;
  return true;
}

function safeParseParameter(value: unknown): DynamicParameter | null {
  const result = dynamicParameterSchema.safeParse(value);
  return result.success ? result.data : null;
}

function safeParseAction(value: unknown): DynamicAction | null {
  const result = dynamicActionSchema.safeParse(value);
  return result.success ? result.data : null;
}

// ═══════════════════════════════════════════════════════════════════════
// Parameter handlers
// ═══════════════════════════════════════════════════════════════════════

export function handleAddDynamicParameter(
  document: DrawingDocument,
  command: AddDynamicParameterCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  const parameter = safeParseParameter(command.parameter);
  if (!parameter) return document;
  try {
    const { updatedDocument } = addDynamicParameterMutation({
      document,
      blockName: command.blockName,
      parameter,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleUpdateDynamicParameter(
  document: DrawingDocument,
  command: UpdateDynamicParameterCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = updateDynamicParameterMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      updates: command.updates as Partial<DynamicParameter>,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleRemoveDynamicParameter(
  document: DrawingDocument,
  command: RemoveDynamicParameterCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = removeDynamicParameterMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Action handlers
// ═══════════════════════════════════════════════════════════════════════

export function handleAddDynamicAction(
  document: DrawingDocument,
  command: AddDynamicActionCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  const action = safeParseAction(command.action);
  if (!action) return document;
  try {
    const { updatedDocument } = addDynamicActionMutation({
      document,
      blockName: command.blockName,
      action,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleUpdateDynamicAction(
  document: DrawingDocument,
  command: UpdateDynamicActionCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = updateDynamicActionMutation({
      document,
      blockName: command.blockName,
      actionId: command.actionId,
      updates: command.updates as Partial<DynamicAction>,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleRemoveDynamicAction(
  document: DrawingDocument,
  command: RemoveDynamicActionCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = removeDynamicActionMutation({
      document,
      blockName: command.blockName,
      actionId: command.actionId,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Visibility state handlers
// ═══════════════════════════════════════════════════════════════════════

export function handleAddVisibilityState(
  document: DrawingDocument,
  command: AddVisibilityStateCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = addVisibilityStateMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      stateId: command.stateId,
      stateName: command.stateName,
      invisibleEntityIds: command.invisibleEntityIds,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleRenameVisibilityState(
  document: DrawingDocument,
  command: RenameVisibilityStateCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = renameVisibilityStateMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      stateId: command.stateId,
      newName: command.newName,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleDeleteVisibilityState(
  document: DrawingDocument,
  command: DeleteVisibilityStateCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = deleteVisibilityStateMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      stateId: command.stateId,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleReorderVisibilityStates(
  document: DrawingDocument,
  command: ReorderVisibilityStatesCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = reorderVisibilityStatesMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      stateIds: command.stateIds,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleSetDefaultVisibilityState(
  document: DrawingDocument,
  command: SetDefaultVisibilityStateCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = setDefaultVisibilityStateMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      stateId: command.stateId,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleToggleVisibilityStateEntity(
  document: DrawingDocument,
  command: ToggleVisibilityStateEntityCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = toggleVisibilityStateEntityMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      stateId: command.stateId,
      entityId: command.entityId,
      visible: command.visible,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Phase K.4 — set current state + BVHIDE / BVSHOW / BVSHOW _All
// ═══════════════════════════════════════════════════════════════════════

export function handleSetCurrentVisibilityState(
  document: DrawingDocument,
  command: SetCurrentVisibilityStateCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = setCurrentVisibilityStateMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      stateId: command.stateId,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleBvhide(
  document: DrawingDocument,
  command: BvhideCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = bvhideMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      stateId: command.stateId,
      entityIds: command.entityIds,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleBvshow(
  document: DrawingDocument,
  command: BvshowCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = bvshowMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      stateId: command.stateId,
      entityIds: command.entityIds,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleBvshowAll(
  document: DrawingDocument,
  command: BvshowAllCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = bvshowAllMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      entityIds: command.entityIds,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Chain group handlers (DYN-3 Batch C)
// ═══════════════════════════════════════════════════════════════════════

export function handleCreateChainGroup(
  document: DrawingDocument,
  command: CreateChainGroupCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = createChainGroupMutation({
      document,
      blockName: command.blockName,
      actionIds: [...command.actionIds],
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleAddActionToChainGroup(
  document: DrawingDocument,
  command: AddActionToChainGroupCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = addActionToChainGroupMutation({
      document,
      blockName: command.blockName,
      actionId: command.actionId,
      ...(command.groupIndex !== undefined
        ? { groupIndex: command.groupIndex }
        : {}),
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleRemoveActionFromChain(
  document: DrawingDocument,
  command: RemoveActionFromChainCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = removeActionFromChainMutation({
      document,
      blockName: command.blockName,
      actionId: command.actionId,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleReorderChainGroup(
  document: DrawingDocument,
  command: ReorderChainGroupCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = reorderChainGroupMutation({
      document,
      blockName: command.blockName,
      groupIndex: command.groupIndex,
      actionIds: [...command.actionIds],
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Geometric constraint handlers (DYN-3 Batch E)
// ═══════════════════════════════════════════════════════════════════════

function safeParseGeometricConstraint(
  value: unknown,
): GeometricConstraint | null {
  const result = geometricConstraintSchema.safeParse(value);
  return result.success ? result.data : null;
}

export function handleAddGeometricConstraint(
  document: DrawingDocument,
  command: AddGeometricConstraintCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  const constraint = safeParseGeometricConstraint(command.constraint);
  if (!constraint) return document;
  try {
    const { updatedDocument } = addGeometricConstraintMutation({
      document,
      blockName: command.blockName,
      constraint,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}
